import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { useFilters } from "@/hooks/useFilters";
import PageHeader from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import DataTable from "@/components/common/DataTable";
import Pagination from "@/components/common/Pagination";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import LeadEditDialog from "@/components/leads/LeadEditDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Plus, Search, Contact as ContactIcon, Pencil, Trash2, X } from "lucide-react";

function AdminActions({ row, ctx, onEdit, onDelete }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(row); };
  const sfx = ctx === "card" ? `card-${row.id}` : row.id;
  return (
    <div className="flex items-center gap-1 justify-end">
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-teal" onClick={stop(onEdit)} data-testid={`lead-edit-btn-${sfx}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-rose-600" onClick={stop(onDelete)} data-testid={`lead-delete-btn-${sfx}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

export default function Leads() {
  const { user } = useAuth();
  const meta = useMeta();
  const navigate = useNavigate();
  const { filters, setFilters, apiParams, params } = useFilters();
  const status = params.get("status") || "all";
  const source = params.get("source") || "all";
  const noAction = params.get("no_action") === "1";
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const isAdmin = user?.role === "super_admin";
  const canCreate = ["super_admin", "manager", "field_executive", "sales_partner"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const p = { ...apiParams, page, page_size: 15 };
      if (q) p.q = q;
      if (status !== "all") p.status = status;
      if (source !== "all") p.source = source;
      if (noAction) p.no_action = true;
      const { data } = await api.get("/leads", { params: p });
      setData(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [apiParams, q, status, source, noAction, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [apiParams, status, source, noAction]);

  const doDelete = async () => {
    try {
      await api.delete(`/leads/${deleting.id}`);
      toast.success("Lead deleted");
      setDeleting(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    {
      key: "org_name", label: "Organization",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-slate-800 truncate">{r.org_name}</div>
          <div className="text-xs text-slate-400">{r.lead_type} · {r.contact_person || "—"}</div>
        </div>
      ),
    },
    { key: "mobile", label: "Mobile", mobileHide: true, render: (r) => r.mobile || "—" },
    { key: "city", label: "City", render: (r) => r.city || "—" },
    { key: "source", label: "Source", mobileHide: true, render: (r) => r.source || "—" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "owner_name", label: "Owner", mobileHide: true, render: (r) => r.owner_name || (r.partner_id ? "Partner lead" : "Unassigned") },
    { key: "created_at", label: "Created", mobileHide: true, render: (r) => fmtDate(r.created_at) },
    ...(isAdmin ? [{ key: "actions", label: "", render: (r, ctx) => <AdminActions row={r} ctx={ctx} onEdit={setEditing} onDelete={setDeleting} /> }] : []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads"
        subtitle="Doctor, clinic and hospital leads with duplicate protection."
        testid="leads-header"
        actions={canCreate && (
          <Button onClick={() => navigate("/leads/new")} className="bg-teal hover:bg-teal-700 h-11" data-testid="add-lead-btn">
            <Plus className="h-4 w-4" /> Add New Lead
          </Button>
        )}
      />

      <FilterBar filters={filters} setFilters={setFilters} testid="leads-filter-bar" />

      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-4 flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search name, mobile, email, city…"
            className="pl-9 h-11" data-testid="leads-search-input" />
        </div>
        <Select value={status} onValueChange={(v) => setFilters({ status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-full sm:w-48 h-11" data-testid="leads-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(meta.lead_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={(v) => setFilters({ source: v === "all" ? "" : v })}>
          <SelectTrigger className="w-full sm:w-44 h-11" data-testid="leads-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {(meta.lead_sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {noAction && (
          <button onClick={() => setFilters({ no_action: "" })} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 h-11 text-sm font-medium text-amber-700 hover:bg-amber-100" data-testid="leads-no-action-chip">
            Without next action <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="text-sm text-slate-500" data-testid="leads-total">{data.total} lead{data.total === 1 ? "" : "s"}</div>

      <DataTable
        columns={columns}
        rows={data.items}
        loading={loading}
        error={error}
        onRetry={load}
        onRowClick={(r) => navigate(`/leads/${r.id}`)}
        testid="leads-table"
        empty={{
          title: "No leads found",
          description: canCreate ? "Try different filters or add a new lead." : "Leads will appear here once assigned to you.",
          icon: ContactIcon,
          action: canCreate && <Button onClick={() => navigate("/leads/new")} className="bg-teal hover:bg-teal-700"><Plus className="h-4 w-4" /> Add New Lead</Button>,
        }}
      />
      <Pagination page={page} pageSize={15} total={data.total} onPage={setPage} />

      {isAdmin && <LeadEditDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} lead={editing} onSaved={load} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} destructive
        title="Delete this lead?" confirmLabel="Delete lead" onConfirm={doDelete} testid="lead-delete-dialog"
        description={`"${deleting?.org_name}" will be removed from all lists (soft delete, kept in the audit trail).`} />
    </div>
  );
}
